import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Send, CheckCircle2, CalendarCheck } from "lucide-react";
import useSEO from "../hooks/useSEO";
import { api } from "../lib/api";

const DEFAULT_HOURS = [
  { dag: "Maandag", uren: "09:00 – 12:00 / 13:00 – 18:30" },
  { dag: "Dinsdag", uren: "09:00 – 12:00 / 13:00 – 18:30" },
  { dag: "Woensdag", uren: "09:00 – 12:00 / 13:00 – 18:30" },
  { dag: "Donderdag", uren: "09:00 – 12:00 / 13:00 – 18:30" },
  { dag: "Vrijdag", uren: "09:00 – 12:00 / 13:00 – 18:30" },
  { dag: "Zaterdag", uren: "09:00 – 12:00" },
  { dag: "Zondag", uren: "Gesloten" },
];

const DEFAULTS = {
  telefoon: "0485 74 15 24",
  email: "TTmotorsports@outlook.com",
  adres: "Beekstraat 4C, 8820 Torhout",
  openingsuren: DEFAULT_HOURS,
};

const telHref = (t) => "tel:" + (t || "").replace(/[^0-9+]/g, "");

export default function Contact() {
  useSEO({
    title: "Contact TT Motorsports Torhout — motor garage Beekstraat 4C",
    description: "Contacteer TT Motorsports in Torhout. Beekstraat 4C, 8820 Torhout. Bel 0485 74 15 24 voor motoronderhoud, herstellingen, onderdelen of een afspraak. Jouw motor garage in de regio West-Vlaanderen.",
    keywords: "contact TT Motorsports, motor garage Torhout, motor onderhoud in de buurt, motor herstelling nabij, motor specialist West-Vlaanderen",
  });

  const [info, setInfo] = useState(DEFAULTS);
  const [form, setForm] = useState({ naam: "", email: "", telefoon: "", onderwerp: "", bericht: "", honeypot: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | done | error
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Load editable contact details, but NEVER gate rendering on this — always fall back to defaults.
  useEffect(() => {
    let mounted = true;
    api.get("/settings")
      .then((r) => {
        if (!mounted || !r?.data) return;
        setInfo({
          telefoon: r.data.telefoon || DEFAULTS.telefoon,
          email: r.data.email || DEFAULTS.email,
          adres: r.data.adres || DEFAULTS.adres,
          openingsuren: r.data.openingsuren?.length ? r.data.openingsuren : DEFAULT_HOURS,
        });
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.naam || !form.email || !form.onderwerp || !form.bericht) return;
    setStatus("sending");
    try {
      await api.post("/contact", form);
      setStatus("done");
      setForm({ naam: "", email: "", telefoon: "", onderwerp: "", bericht: "", honeypot: "" });
    } catch {
      setStatus("error");
    }
  };

  const inputCls = "w-full bg-[#0A0A0B] border border-[#2A2B31] px-4 py-3 text-white focus:outline-none focus:border-[#E10600] transition-colors";
  const [adresLine1, ...adresRest] = (info.adres || "").split(",");
  const adresLine2 = adresRest.join(",").trim();

  return (
    <div data-testid="contact-page" className="bg-[#0A0A0B]">
      <section className="pt-36 pb-12 bg-[#0A0A0B] border-b border-[#2A2B31] relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-full racing-stripe opacity-[0.04]" />
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 relative">
          <span className="font-mono-tt uppercase text-xs tracking-[0.3em] text-[#E10600]">Contact</span>
          <h1 className="heading-xl text-5xl md:text-7xl text-white mt-3">Neem contact op</h1>
          <p className="text-[#9A9CA3] text-lg mt-4 max-w-2xl">Vragen, een afspraak maken of interesse in een motor? We helpen je graag verder.</p>
          <div className="flex flex-wrap gap-3 mt-7">
            <Link to="/afspraak" data-testid="contact-afspraak-knop"
              className="bg-[#E10600] hover:bg-[#B80500] text-white font-display uppercase font-700 tracking-wide px-6 py-3.5 flex items-center gap-2 transition-colors">
              <CalendarCheck size={18} /> Maak een afspraak
            </Link>
            <a href={telHref(info.telefoon)} data-testid="contact-hero-bel"
              className="border border-[#2A2B31] hover:border-white text-white font-display uppercase font-700 tracking-wide px-6 py-3.5 flex items-center gap-2 transition-colors">
              <Phone size={18} /> {info.telefoon}
            </a>
          </div>
        </div>
      </section>

      <section className="section-pad bg-[#0A0A0B]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-12">
          {/* Form */}
          <div>
            <h2 className="heading-xl text-3xl text-white mb-6">Stuur een bericht</h2>
            {status === "done" ? (
              <div data-testid="contact-success" className="border border-[#E10600] bg-[#E10600]/10 p-8 text-center">
                <CheckCircle2 size={44} className="text-[#E10600] mx-auto mb-4" />
                <p className="font-display uppercase font-700 text-2xl text-white mb-2">Bedankt!</p>
                <p className="text-[#B8B9BE]">Je bericht is verzonden. We nemen zo snel mogelijk contact met je op.</p>
                <button onClick={() => setStatus("idle")} className="mt-6 text-[#E10600] font-display uppercase text-sm">Nog een bericht sturen</button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="space-y-4">
                <input type="text" tabIndex={-1} autoComplete="off" value={form.honeypot} onChange={(e) => set("honeypot", e.target.value)}
                  className="hidden" aria-hidden="true" />
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="font-mono-tt text-xs uppercase tracking-wider text-[#9A9CA3] block mb-1.5">Naam *</label>
                    <input data-testid="contact-naam" required value={form.naam} onChange={(e) => set("naam", e.target.value)} className={inputCls} />
                  </div>
                  <div>
                    <label className="font-mono-tt text-xs uppercase tracking-wider text-[#9A9CA3] block mb-1.5">E-mail *</label>
                    <input data-testid="contact-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="font-mono-tt text-xs uppercase tracking-wider text-[#9A9CA3] block mb-1.5">Telefoon</label>
                    <input data-testid="contact-telefoon" type="tel" inputMode="tel" autoComplete="tel" value={form.telefoon} onChange={(e) => set("telefoon", e.target.value)} className={inputCls} />
                  </div>
                  <div>
                    <label className="font-mono-tt text-xs uppercase tracking-wider text-[#9A9CA3] block mb-1.5">Onderwerp *</label>
                    <input data-testid="contact-onderwerp" required value={form.onderwerp} onChange={(e) => set("onderwerp", e.target.value)} className={inputCls} />
                  </div>
                </div>
                <div>
                  <label className="font-mono-tt text-xs uppercase tracking-wider text-[#9A9CA3] block mb-1.5">Bericht *</label>
                  <textarea data-testid="contact-bericht" required rows={5} value={form.bericht} onChange={(e) => set("bericht", e.target.value)} className={inputCls} />
                </div>
                {status === "error" && <p className="text-[#E10600] text-sm">Er ging iets mis. Probeer opnieuw of bel ons.</p>}
                <button data-testid="contact-submit" type="submit" disabled={status === "sending"}
                  className="bg-[#E10600] hover:bg-[#B80500] disabled:opacity-60 text-white font-display uppercase font-700 tracking-wide px-8 py-4 w-full sm:w-auto flex items-center justify-center gap-2 transition-colors">
                  {status === "sending" ? "Versturen..." : <>Verstuur bericht <Send size={17} /></>}
                </button>
              </form>
            )}
          </div>

          {/* Info + Map */}
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-3">
              <a href={telHref(info.telefoon)} data-testid="contact-bel-knop"
                className="bg-[#E10600] hover:bg-[#B80500] text-white font-display uppercase font-700 tracking-wide px-4 py-4 flex items-center justify-center gap-2 transition-colors">
                <Phone size={18} /> Bel ons
              </a>
              <a href={`mailto:${info.email}`} data-testid="contact-mail-knop"
                className="border border-[#2A2B31] hover:border-white text-white font-display uppercase font-700 tracking-wide px-4 py-4 flex items-center justify-center gap-2 transition-colors">
                <Mail size={18} /> E-mail ons
              </a>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="bg-[#141518] border border-[#2A2B31] p-5">
                <MapPin size={22} className="text-[#E10600] mb-3" />
                <p className="font-display uppercase font-700 text-white mb-1">Adres</p>
                <p className="text-[#9A9CA3] text-sm">{adresLine1}{adresLine2 && <><br />{adresLine2}</>}</p>
              </div>
              <div className="bg-[#141518] border border-[#2A2B31] p-5">
                <Phone size={22} className="text-[#E10600] mb-3" />
                <p className="font-display uppercase font-700 text-white mb-1">Telefoon</p>
                <a href={telHref(info.telefoon)} data-testid="contact-phone-link" className="inline-block py-2 text-[#9A9CA3] text-base md:text-sm hover:text-white">{info.telefoon}</a>
              </div>
              <div className="bg-[#141518] border border-[#2A2B31] p-5">
                <Mail size={22} className="text-[#E10600] mb-3" />
                <p className="font-display uppercase font-700 text-white mb-1">E-mail</p>
                <a href={`mailto:${info.email}`} data-testid="contact-email-link" className="inline-block py-2 text-[#9A9CA3] text-base md:text-sm hover:text-white break-all">{info.email}</a>
              </div>
              <div className="bg-[#141518] border border-[#2A2B31] p-5">
                <Clock size={22} className="text-[#E10600] mb-3" />
                <p className="font-display uppercase font-700 text-white mb-1">Open</p>
                <p className="text-[#9A9CA3] text-sm">Ma–Vr 9–18:30<br />Za 9–12</p>
              </div>
            </div>

            <div className="bg-[#141518] border border-[#2A2B31] p-5">
              <p className="font-display uppercase font-700 text-white mb-3 flex items-center gap-2"><Clock size={18} className="text-[#E10600]" /> Openingsuren</p>
              <ul className="space-y-1.5">
                {(info.openingsuren || DEFAULT_HOURS).map((o) => (
                  <li key={o.dag} className="flex justify-between text-sm font-mono-tt">
                    <span className="text-[#9A9CA3]">{o.dag}</span>
                    <span className={/gesloten/i.test(o.uren) ? "text-[#E10600]" : "text-white"}>{o.uren}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="aspect-video border border-[#2A2B31] overflow-hidden bg-[#141518]" data-testid="google-map">
              <iframe
                title="TT Motorsports locatie"
                src="https://www.google.com/maps?q=Beekstraat+4C,+8820+Torhout,+Belgium&output=embed"
                width="100%" height="100%" style={{ border: 0 }}
                loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
